import React, { useEffect, useState } from 'react';
import { DoorOpen, Loader2, RefreshCcw, ShieldAlert } from 'lucide-react';

import api, { getApiErrorMessage } from '../../services/api';
import VideoAbertura from './VideoAbertura';
import { FeedbackBanner, LoadingState, PageHeader, PageLayout } from '../../components/ui/PageLayout';

export default function AberturaPage() {
  const [faciais, setFaciais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openingId, setOpeningId] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const response = await api.get('/abertura/faciais');
      setFaciais(response.data || []);
    } catch (error) {
      setFeedback({ type: 'error', message: getApiErrorMessage(error, 'Erro ao carregar faciais para abertura.') });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const abrirPorta = async (facial) => {
    const confirmou = window.confirm(`Abrir pelo dispositivo "${facial.nome}"? Use este comando apenas em emergência ou liberação assistida.`);
    if (!confirmou) return;

    try {
      setOpeningId(facial.id);
      setFeedback(null);
      const response = await api.post(`/abertura/faciais/${facial.id}/abrir`);
      setFeedback({ type: 'success', message: response.data?.message || `Comando enviado para ${facial.nome}.` });
    } catch (error) {
      setFeedback({ type: 'error', message: getApiErrorMessage(error, `Não foi possível abrir pela facial ${facial.nome}.`) });
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <PageLayout>
      <PageHeader
        title="Abertura Remota"
        description="Liberação Remota assistida"
        actions={(
          <button
            type="button"
            onClick={load}
            disabled={loading || openingId !== null}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCcw size={18} />
            Atualizar
          </button>
        )}
      />

      <FeedbackBanner type={feedback?.type} message={feedback?.message} onClose={() => setFeedback(null)} />

      {loading ? (
        <LoadingState message="Carregando faciais..." />
      ) : faciais.length ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {faciais.map((facial) => {
            const opening = openingId === facial.id;
            return (
              <article key={facial.id} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h2 className="break-words text-lg font-black text-slate-800">{facial.nome}</h2>
                    <p className="mt-1 text-sm font-semibold capitalize text-slate-500">{facial.tipo_label || facial.marca || 'sem marca'}</p>
                  </div>
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                    <DoorOpen size={22} />
                  </div>
                </div>

                <div className="mt-4 space-y-2 text-sm text-slate-600">
                  <div className="flex flex-wrap gap-2">
                    {(facial.grupos || []).length ? (facial.grupos || []).map((grupo) => (
                      <span key={grupo.id} className="rounded-full bg-sky-50 px-2.5 py-1 text-xs font-bold text-sky-700">
                        {grupo.nome}
                      </span>
                    )) : (
                      <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-500">Sem grupo</span>
                    )}
                  </div>
                </div>

                {facial.abertura_com_video && facial.ativo !== false && <VideoAbertura dispositivoId={facial.id} nome={facial.nome} />}

                <button
                  type="button"
                  onClick={() => abrirPorta(facial)}
                  disabled={openingId !== null || facial.ativo === false}
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-condo-dark px-4 py-3 text-sm font-black text-white shadow-sm transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {opening ? <Loader2 className="animate-spin" size={18} /> : <DoorOpen size={18} />}
                  {facial.ativo === false ? 'Dispositivo desativado' : opening ? 'Enviando comando...' : 'Abrir porta'}
                </button>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-12 text-center text-slate-500">
          <ShieldAlert className="mx-auto mb-3 text-slate-400" size={32} />
          Nenhuma facial cadastrada para abertura.
        </div>
      )}
    </PageLayout>
  );
}
